import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  flowsQuery,
  IFlow,
  IWork,
  useIsOnline,
  useEnvironment,
  useNotifications,
  useSidePeekDrawer,
} from '@diana/core';
import { routeUrls } from '../../app/Routing/RouteUrls';
import { WORKBOARD_PAGE_NS } from './i18n';
import { useWorkboardCreateWorkOffline } from './useWorkboardCreateWorkOffline';

interface useWorkBoardCreateWorkProps {
  setProcessing: (status: boolean) => void;
  onCreated: (work: IWork) => void;
  flow: IFlow;
  showSidePeek?: boolean;
}

export const useWorkBoardCreateWork = ({
  flow,
  setProcessing,
  onCreated,
  showSidePeek,
}: useWorkBoardCreateWorkProps) => {
  const navigate = useNavigate();
  const environment = useEnvironment();
  const { error } = useNotifications();
  const { openWorkDrawer } = useSidePeekDrawer();
  const { t } = useTranslation(WORKBOARD_PAGE_NS);

  const flowId = flow.id;

  const isOnline = useIsOnline();

  const { canCreateWorkOffline, createWorkOffline } = useWorkboardCreateWorkOffline({ flowId });

  const { fn } = flowsQuery.startAt({ id: flow.id });
  return async (stepName: string | undefined) => {
    if (!stepName) return console.warn(`[useWorkBoardCreateWork] - stepName not provided`);

    setProcessing(true);

    try {
      const work =
        !isOnline && canCreateWorkOffline ? await createWorkOffline(stepName) : await fn(stepName);

      onCreated(work);
      setProcessing(false);

      if (showSidePeek) {
        openWorkDrawer({
          workId: work.id,
          flowId: flow.id,
          displayName: work.displayName || work.workCode || '',
        });
      } else {
        navigate(routeUrls.work.url({ environment, workId: work.id }, {}));
      }
    } catch (e) {
      setProcessing(false);
      error({ message: t('errors.cannotCreateWork') });
      console.error(`Cannot start work: ${e}`);
    }
  };
};
